import { HttpClient } from '@adapters/HttpClient';
import type {
  ProfitabilityAdCampaignItemResponse,
  ProfitabilityAdCampaignPatchPayload,
  ProfitabilityAdCampaignsListResponse,
  ProfitabilityAdSpendImportResponse,
  ProfitabilityByProductResponse,
  ProfitabilityCommonFilters,
  ProfitabilityDefaultShippingCostItemResponse,
  ProfitabilityDefaultShippingCostsListResponse,
  ProfitabilityDefaultShippingCostPayload,
  ProfitabilityMissingDefaultShippingCostsQuery,
  ProfitabilityMissingDefaultShippingCostsResponse,
  ProfitabilityOrderDetailResponse,
  ProfitabilityOrdersListResponse,
  ProfitabilityOrdersQuery,
  ProfitabilityProductCostItemResponse,
  ProfitabilityProductCostPayload,
  ProfitabilityProductCostsListResponse,
  ProfitabilityProductCostsQuery,
  ProfitabilityProductsWithoutCostQuery,
  ProfitabilityProductsWithoutCostResponse,
  ProfitabilitySummaryOrdersQuery,
  ProfitabilitySummaryOrdersResponse,
  ProfitabilitySummaryBreakdownQuery,
  ProfitabilitySummaryCogsResponse,
  ProfitabilitySummaryDailyQuery,
  ProfitabilitySummaryDailyResponse,
  ProfitabilitySummaryResponse,
  ProfitabilitySummaryShippingCostsResponse,
} from '../models/Profitability';

function buildCommonParams(filters?: ProfitabilityCommonFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (!filters) return params;
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  if (filters.date_column) params.date_column = filters.date_column;
  if (filters.shop_id != null) params.shop_id = filters.shop_id;
  return params;
}

function buildSummaryBreakdownParams(query?: ProfitabilitySummaryBreakdownQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    ...buildCommonParams(query),
  };
  if (query?.payment_method_id != null) params.payment_method_id = query.payment_method_id;
  if (query?.shipment_status_id != null) params.shipment_status_id = query.shipment_status_id;
  if (query?.page != null) params.page = query.page;
  if (query?.per_page != null) params.per_page = Math.min(query.per_page, 100);
  return params;
}

export class ProfitabilityApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getSummary(filters?: ProfitabilityCommonFilters): Promise<ProfitabilitySummaryResponse> {
    return this.httpClient.get<ProfitabilitySummaryResponse>('/profitability/summary', buildCommonParams(filters));
  }

  async getSummaryDaily(query?: ProfitabilitySummaryDailyQuery): Promise<ProfitabilitySummaryDailyResponse> {
    const params: Record<string, string | number> = {
      ...buildCommonParams(query),
    };
    if (query?.payment_method_id != null) params.payment_method_id = query.payment_method_id;
    if (query?.shipment_status_id != null) params.shipment_status_id = query.shipment_status_id;
    return this.httpClient.get<ProfitabilitySummaryDailyResponse>(
      '/profitability/summary/daily',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }

  async getSummaryCogs(query?: ProfitabilitySummaryBreakdownQuery): Promise<ProfitabilitySummaryCogsResponse> {
    const params = buildSummaryBreakdownParams(query);
    return this.httpClient.get<ProfitabilitySummaryCogsResponse>(
      '/profitability/summary/cogs',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }

  async getSummaryShippingCosts(
    query?: ProfitabilitySummaryBreakdownQuery,
  ): Promise<ProfitabilitySummaryShippingCostsResponse> {
    const params = buildSummaryBreakdownParams(query);
    return this.httpClient.get<ProfitabilitySummaryShippingCostsResponse>(
      '/profitability/summary/shipping-costs',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }

  async getSummaryOrders(query?: ProfitabilitySummaryOrdersQuery): Promise<ProfitabilitySummaryOrdersResponse> {
    const params: Record<string, string | number> = {
      ...buildCommonParams(query),
    };
    if (query?.page != null) params.page = query.page;
    if (query?.per_page != null) params.per_page = Math.min(query.per_page, 100);
    if (query?.payment_method_id != null) params.payment_method_id = query.payment_method_id;
    if (query?.shipment_status_id != null) params.shipment_status_id = query.shipment_status_id;
    return this.httpClient.get<ProfitabilitySummaryOrdersResponse>('/profitability/summary/orders', params);
  }

  async getOrders(query?: ProfitabilityOrdersQuery): Promise<ProfitabilityOrdersListResponse> {
    const params: Record<string, string | number> = {
      ...buildCommonParams(query),
    };
    if (query?.page != null) params.page = query.page;
    if (query?.per_page != null) params.per_page = Math.min(query.per_page, 100);
    return this.httpClient.get<ProfitabilityOrdersListResponse>('/profitability/orders', params);
  }

  async getOrderById(orderId: number, filters?: ProfitabilityCommonFilters): Promise<ProfitabilityOrderDetailResponse> {
    return this.httpClient.get<ProfitabilityOrderDetailResponse>(
      `/profitability/orders/${orderId}`,
      buildCommonParams(filters),
    );
  }

  async getByProduct(filters?: ProfitabilityCommonFilters): Promise<ProfitabilityByProductResponse> {
    return this.httpClient.get<ProfitabilityByProductResponse>('/profitability/by-product', buildCommonParams(filters));
  }

  async getDefaultShippingCosts(teamId?: number): Promise<ProfitabilityDefaultShippingCostsListResponse> {
    const params = teamId != null ? { team_id: teamId } : undefined;
    return this.httpClient.get<ProfitabilityDefaultShippingCostsListResponse>(
      '/profitability/default-shipping-costs',
      params,
    );
  }

  async createDefaultShippingCost(
    body: ProfitabilityDefaultShippingCostPayload,
  ): Promise<ProfitabilityDefaultShippingCostItemResponse> {
    return this.httpClient.post<ProfitabilityDefaultShippingCostItemResponse>(
      '/profitability/default-shipping-costs',
      body,
    );
  }

  async updateDefaultShippingCost(
    id: number,
    body: ProfitabilityDefaultShippingCostPayload,
  ): Promise<ProfitabilityDefaultShippingCostItemResponse> {
    return this.httpClient.put<ProfitabilityDefaultShippingCostItemResponse>(
      `/profitability/default-shipping-costs/${id}`,
      body,
    );
  }

  async deleteDefaultShippingCost(id: number): Promise<void> {
    await this.httpClient.delete(`/profitability/default-shipping-costs/${id}`);
  }

  async getMissingDefaultShippingCosts(
    query?: ProfitabilityMissingDefaultShippingCostsQuery,
  ): Promise<ProfitabilityMissingDefaultShippingCostsResponse> {
    const params: Record<string, number> = {};
    if (query?.team_id != null) params.team_id = query.team_id;
    if (query?.page != null) params.page = query.page;
    if (query?.per_page != null) params.per_page = Math.min(query.per_page, 100);
    return this.httpClient.get<ProfitabilityMissingDefaultShippingCostsResponse>(
      '/profitability/default-shipping-costs/missing',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }

  async getAdSpendCampaigns(): Promise<ProfitabilityAdCampaignsListResponse> {
    return this.httpClient.get<ProfitabilityAdCampaignsListResponse>('/profitability/ad-spend/campaigns');
  }

  async importAdSpend(file: File): Promise<ProfitabilityAdSpendImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<ProfitabilityAdSpendImportResponse>('/profitability/ad-spend/import', formData);
  }

  async patchAdSpendCampaign(
    id: number,
    body: ProfitabilityAdCampaignPatchPayload,
  ): Promise<ProfitabilityAdCampaignItemResponse> {
    return this.httpClient.patch<ProfitabilityAdCampaignItemResponse>(`/profitability/ad-spend/campaigns/${id}`, body);
  }

  async getProductCosts(query?: ProfitabilityProductCostsQuery): Promise<ProfitabilityProductCostsListResponse> {
    const params: Record<string, number> = {};
    if (query?.product_id != null) params.product_id = query.product_id;
    return this.httpClient.get<ProfitabilityProductCostsListResponse>(
      '/profitability/product-costs',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }

  async createProductCost(body: ProfitabilityProductCostPayload): Promise<ProfitabilityProductCostItemResponse> {
    return this.httpClient.post<ProfitabilityProductCostItemResponse>('/profitability/product-costs', body);
  }

  async updateProductCost(
    id: number,
    body: ProfitabilityProductCostPayload,
  ): Promise<ProfitabilityProductCostItemResponse> {
    return this.httpClient.put<ProfitabilityProductCostItemResponse>(`/profitability/product-costs/${id}`, body);
  }

  async deleteProductCost(id: number): Promise<void> {
    await this.httpClient.delete(`/profitability/product-costs/${id}`);
  }

  async getProductsWithoutCost(
    query?: ProfitabilityProductsWithoutCostQuery,
  ): Promise<ProfitabilityProductsWithoutCostResponse> {
    const params: Record<string, string | number> = {};
    if (query?.search?.trim()) params.search = query.search.trim();
    if (query?.page != null) params.page = query.page;
    if (query?.per_page != null) params.per_page = Math.min(query.per_page, 100);
    return this.httpClient.get<ProfitabilityProductsWithoutCostResponse>(
      '/profitability/products-without-cost',
      Object.keys(params).length > 0 ? params : undefined,
    );
  }
}
